import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import * as XLSX from 'xlsx'

const SAMPLE_TARGET = 10

export default function MonthlyAuditSample() {
  const navigate = useNavigate()
  const [records, setRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<any>(null)
  const [formData, setFormData] = useState({
    audit_month: new Date().toISOString().split('T')[0].slice(0, 7),
    auditor_name: '',
    job_ref: '',
    importer_name: '',
    hs_codes_checked: '',
    declaration_value: '',
    findings: '',
    passed: true,
    issues_found: '',
    follow_up_required: false,
    mrn: '',
    agent_name: '',
    cartons: '',
    gross_weight: '',
    net_weight: '',
    eori_number: '',
    currency: 'USD'
  })
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().split('T')[0].slice(0, 7))

  useEffect(() => {
    loadRecords()
    getUser()
  }, [selectedMonth])

  async function getUser() {
    try {
      const { data: { user }, error } = await supabase.auth.getUser()
      if (error) {
        console.error('[getUser] Auth error:', error)
        setUserEmail('unknown@example.com')
      } else if (user?.email) {
        console.log('[getUser] Authenticated user:', user.email)
        setUserEmail(user.email)
      } else {
        console.warn('[getUser] No user email found')
        setUserEmail('unknown@example.com')
      }
    } catch (err) {
      console.error('[getUser] Exception:', err)
      setUserEmail('unknown@example.com')
    }
  }

  async function loadRecords() {
    setLoading(true)
    try {
      let query = supabase.from('monthly_audit_samples').select('*')

      if (selectedMonth) {
        const startDate = `${selectedMonth}-01`
        // Calculate next month for end date
        const [year, month] = selectedMonth.split('-')
        const nextMonth = parseInt(month) === 12
          ? `${parseInt(year) + 1}-01`
          : `${year}-${String(parseInt(month) + 1).padStart(2, '0')}`
        const endDate = `${nextMonth}-01`

        query = query.gte('audit_month', startDate).lt('audit_month', endDate)
      }

      const { data, error } = await query.order('audit_month', { ascending: false })

      if (error) {
        console.error('Supabase query error:', error)
        throw error
      }

      console.log(`[loadRecords] Loaded ${data?.length || 0} records for month ${selectedMonth}`)
      setRecords(data || [])
    } catch (error: any) {
      console.error('Error loading records:', error)
      setError(`Failed to load records: ${error?.message || error?.error_description || String(error) || 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      // Validate required fields
      if (!formData.auditor_name.trim()) {
        setError('Auditor Name is required')
        return
      }
      if (!formData.job_ref.trim()) {
        setError('Job Reference is required')
        return
      }
      if (!formData.importer_name.trim()) {
        setError('Importer Name is required')
        return
      }
      if (!formData.hs_codes_checked.trim()) {
        setError('HS Codes Checked is required')
        return
      }

      // Convert month format from "2026-05" to "2026-05-01" for Postgres DATE type
      const auditDateStr = formData.audit_month.includes('-') && formData.audit_month.length === 7
        ? `${formData.audit_month}-01`
        : formData.audit_month

      const { error } = await supabase.from('monthly_audit_samples').insert([{
        ...formData,
        audit_month: auditDateStr,
        declaration_value: formData.declaration_value ? parseFloat(formData.declaration_value) : null,
        cartons: formData.cartons ? parseInt(formData.cartons) : null,
        gross_weight: formData.gross_weight ? parseFloat(formData.gross_weight) : null,
        net_weight: formData.net_weight ? parseFloat(formData.net_weight) : null,
        created_by: userEmail || 'unknown@example.com'
      }])

      if (error) {
        console.error('Supabase error:', error)
        const errorMsg = error.message || JSON.stringify(error)
        setError(`Failed to save: ${errorMsg}`)
        return
      }

      setShowForm(false)
      setSuccess('Audit record saved successfully!')
      setFormData({
        audit_month: new Date().toISOString().split('T')[0].slice(0, 7),
        auditor_name: '',
        job_ref: '',
        importer_name: '',
        hs_codes_checked: '',
        declaration_value: '',
        findings: '',
        passed: true,
        issues_found: '',
        follow_up_required: false,
        mrn: '',
        agent_name: '',
        cartons: '',
        gross_weight: '',
        net_weight: '',
        eori_number: '',
        currency: 'USD'
      })

      setTimeout(() => setSuccess(''), 3000)
      await loadRecords()
    } catch (error: any) {
      console.error('Error saving record:', error)
      const errorMsg = error?.message || error?.error_description || String(error) || 'Unknown error'
      setError(`Error: ${errorMsg}`)
    }
  }

  function exportToExcel() {
    if (records.length === 0) return

    const headers = ['Audit Month', 'MRN', 'Job Ref', 'Importer', 'Agent', 'Auditor', 'HS Codes', 'Value', 'Currency', 'Cartons', 'Gross Weight', 'Net Weight', 'EORI', 'Findings', 'Result', 'Issues', 'Follow-up']
    const rows = records.map(r => [
      new Date(r.audit_month).toLocaleDateString(),
      r.mrn || '',
      r.job_ref,
      r.importer_name,
      r.agent_name || '',
      r.auditor_name,
      r.hs_codes_checked,
      r.declaration_value || '',
      r.currency || 'USD',
      r.cartons || '',
      r.gross_weight || '',
      r.net_weight || '',
      r.eori_number || '',
      r.findings || '',
      r.passed ? 'Passed' : 'Failed',
      r.issues_found || '',
      r.follow_up_required ? 'Yes' : 'No'
    ])

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Audit Sample')
    XLSX.writeFile(wb, `monthly-audit-${selectedMonth}.xlsx`)
  }

  function formatAuditMonth(auditMonth: string): string {
    try {
      const date = new Date(auditMonth)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    } catch {
      return auditMonth
    }
  }

  function formatDateTime(dateString: string): string {
    try {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = date.toLocaleDateString('en-US', { month: 'short' })
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${day} ${month} ${year} ${hours}:${minutes}`
    } catch {
      return dateString
    }
  }

  async function handleDeleteRecord(id: string) {
    if (!window.confirm('Are you sure you want to delete this record?')) return
    try {
      const { error } = await supabase.from('monthly_audit_samples').delete().eq('id', id)
      if (error) throw error
      await loadRecords()
      setSuccess('Record deleted successfully')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      const errorMsg = err?.message || err?.error_description || String(err) || 'Unknown error'
      setError(`Failed to delete: ${errorMsg}`)
    }
  }

  function handleViewRecord(record: any) {
    setSelectedRecord(record)
    setShowDetailModal(true)
  }

  const monthRecords = records.filter(r => r.audit_month.startsWith(selectedMonth))
  const passedCount = monthRecords.filter(r => r.passed).length
  const passRate = monthRecords.length > 0 ? ((passedCount / monthRecords.length) * 100).toFixed(1) : '0'

  // Z63.3: "days remaining" only makes sense for the live current month -- selectedMonth lets
  // staff browse past months too, where a countdown would be meaningless/confusing.
  const today = new Date()
  const isCurrentMonth = selectedMonth === today.toISOString().slice(0, 7)
  const daysRemainingThisMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate() - today.getDate()
  const behindSchedule = isCurrentMonth && monthRecords.length < SAMPLE_TARGET && daysRemainingThisMonth <= 7

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
          <button
            onClick={() => navigate('/customs')}
            className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            Customs
          </button>
          <span>/</span>
          <span className="text-gray-900 dark:text-white">Monthly Audit Sample</span>
        </div>

        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Monthly Audit Sample</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportToExcel}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Excel
            </button>
            <button
              onClick={() => setShowForm(!showForm)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              {showForm ? 'Cancel' : '+ New Audit'}
            </button>
          </div>
        </div>

        {/* Month Selector and Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="col-span-1">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Select Month</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-600 dark:text-gray-400 text-sm">Pass Rate</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{passRate}%</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-600 dark:text-gray-400 text-sm">Passed</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{passedCount}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-600 dark:text-gray-400 text-sm">Total Audits</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{monthRecords.length}</p>
          </div>
        </div>

        {/* Progress Card -- Z63.3: absorbed the "N/10 samples -- N days remaining" status row
            that used to live (misplaced) on the ETSF Compliance page's status card. */}
        <div className={`rounded-lg shadow-sm border p-6 mb-6 ${
          behindSchedule
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
        }`}>
          <div className="flex justify-between items-baseline mb-3">
            <h3 className={`text-lg font-semibold ${behindSchedule ? 'text-red-900 dark:text-red-100' : 'text-gray-900 dark:text-white'}`}>
              Monthly Audit Progress
            </h3>
            <span className={`text-sm font-medium ${behindSchedule ? 'text-red-800 dark:text-red-200' : 'text-gray-600 dark:text-gray-400'}`}>
              {monthRecords.length} / {SAMPLE_TARGET} samples{isCurrentMonth ? ` — ${daysRemainingThisMonth} day${daysRemainingThisMonth !== 1 ? 's' : ''} remaining` : ''}
            </span>
          </div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-3">
            <div
              className={`h-full rounded-full transition-all ${monthRecords.length >= SAMPLE_TARGET ? 'bg-green-500' : behindSchedule ? 'bg-red-500' : 'bg-blue-500'}`}
              style={{ width: `${Math.min((monthRecords.length / SAMPLE_TARGET) * 100, 100)}%` }}
            />
          </div>
          <p className={`text-sm ${behindSchedule ? 'text-red-800 dark:text-red-200' : 'text-gray-600 dark:text-gray-400'}`}>
            {monthRecords.length >= SAMPLE_TARGET
              ? '✅ Minimum 10 samples reached'
              : behindSchedule
                ? `🔴 Complete ${SAMPLE_TARGET - monthRecords.length} more sample${SAMPLE_TARGET - monthRecords.length !== 1 ? 's' : ''} before end of month`
                : `${SAMPLE_TARGET - monthRecords.length} more sample${SAMPLE_TARGET - monthRecords.length !== 1 ? 's' : ''} needed this month`
            }
          </p>
        </div>

        {/* Error and Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
            {success}
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Record New Audit</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Audit Month</label>
                  <input
                    type="month"
                    value={formData.audit_month}
                    onChange={(e) => setFormData({...formData, audit_month: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Auditor Name</label>
                  <input
                    type="text"
                    value={formData.auditor_name}
                    onChange={(e) => setFormData({...formData, auditor_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Job Reference</label>
                  <input
                    type="text"
                    value={formData.job_ref}
                    onChange={(e) => setFormData({...formData, job_ref: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Importer Name</label>
                  <input
                    type="text"
                    value={formData.importer_name}
                    onChange={(e) => setFormData({...formData, importer_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Declaration Value</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.declaration_value}
                    onChange={(e) => setFormData({...formData, declaration_value: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={formData.passed === true}
                      onChange={() => setFormData({...formData, passed: true})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Passed</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={formData.passed === false}
                      onChange={() => setFormData({...formData, passed: false})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Failed</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">MRN</label>
                  <input
                    type="text"
                    value={formData.mrn}
                    onChange={(e) => setFormData({...formData, mrn: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Agent Name</label>
                  <input
                    type="text"
                    value={formData.agent_name}
                    onChange={(e) => setFormData({...formData, agent_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EORI Number</label>
                  <input
                    type="text"
                    value={formData.eori_number}
                    onChange={(e) => setFormData({...formData, eori_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cartons</label>
                  <input
                    type="number"
                    value={formData.cartons}
                    onChange={(e) => setFormData({...formData, cartons: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gross Weight (KG)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.gross_weight}
                    onChange={(e) => setFormData({...formData, gross_weight: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Net Weight (KG)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.net_weight}
                    onChange={(e) => setFormData({...formData, net_weight: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Currency</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({...formData, currency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="JPY">JPY</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">HS Codes Checked (comma-separated)</label>
                <input
                  type="text"
                  value={formData.hs_codes_checked}
                  onChange={(e) => setFormData({...formData, hs_codes_checked: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Findings</label>
                <textarea
                  value={formData.findings}
                  onChange={(e) => setFormData({...formData, findings: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Issues Found</label>
                <textarea
                  value={formData.issues_found}
                  onChange={(e) => setFormData({...formData, issues_found: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={2}
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.follow_up_required}
                  onChange={(e) => setFormData({...formData, follow_up_required: e.target.checked})}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Follow-up Required</span>
              </label>
              <button
                type="submit"
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Save Audit Record
              </button>
            </form>
          </div>
        )}

        {/* Records Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Audit Month</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Created</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">MRN</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Job Ref</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Importer</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">EORI</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Result</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : monthRecords.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">No audits for this month</td>
                </tr>
              ) : (
                monthRecords.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                    <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">{formatAuditMonth(record.audit_month)}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">{formatDateTime(record.created_at)}</td>
                    <td className="px-4 py-3 font-mono text-gray-900 dark:text-white text-xs">{record.mrn || '—'}</td>
                    <td className="px-4 py-3 font-mono text-gray-900 dark:text-white text-xs">{record.job_ref}</td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">{record.importer_name}</td>
                    <td className="px-4 py-3 font-mono text-gray-900 dark:text-white text-xs">{record.eori_number || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        record.passed
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      }`}>
                        {record.passed ? 'Passed' : 'Failed'}
                      </span>
                    </td>
                    <td className="px-4 py-3 flex gap-2">
                      <button
                        onClick={() => handleViewRecord(record)}
                        className="px-2 py-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded text-xs font-medium"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDeleteRecord(record.id)}
                        className="px-2 py-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded text-xs font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedRecord && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Record Details</h2>
                <button
                  onClick={() => {
                    setShowDetailModal(false)
                    setSelectedRecord(null)
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Audit Month</label>
                    <p className="text-gray-900 dark:text-white font-medium mt-1">{formatAuditMonth(selectedRecord.audit_month)}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Created</label>
                    <p className="text-gray-900 dark:text-white mt-1">{formatDateTime(selectedRecord.created_at)}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">MRN</label>
                    <p className="text-gray-900 dark:text-white font-mono text-sm mt-1">{selectedRecord.mrn || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">EORI Number</label>
                    <p className="text-gray-900 dark:text-white font-mono text-sm mt-1">{selectedRecord.eori_number || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Auditor Name</label>
                    <p className="text-gray-900 dark:text-white mt-1">{selectedRecord.auditor_name}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Agent Name</label>
                    <p className="text-gray-900 dark:text-white mt-1">{selectedRecord.agent_name || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Job Reference</label>
                    <p className="text-gray-900 dark:text-white font-mono text-sm mt-1">{selectedRecord.job_ref}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Importer Name</label>
                    <p className="text-gray-900 dark:text-white mt-1">{selectedRecord.importer_name}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Cartons</label>
                    <p className="text-gray-900 dark:text-white font-medium mt-1">{selectedRecord.cartons || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Gross Weight (KG)</label>
                    <p className="text-gray-900 dark:text-white font-medium mt-1">{selectedRecord.gross_weight || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Net Weight (KG)</label>
                    <p className="text-gray-900 dark:text-white font-medium mt-1">{selectedRecord.net_weight || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Currency</label>
                    <p className="text-gray-900 dark:text-white font-medium mt-1">{selectedRecord.currency || 'USD'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Declaration Value</label>
                    <p className="text-gray-900 dark:text-white font-medium mt-1">{selectedRecord.currency || 'USD'} {selectedRecord.declaration_value || '—'}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">HS Codes Checked</label>
                    <p className="text-gray-900 dark:text-white mt-1">{selectedRecord.hs_codes_checked}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Result</label>
                    <span className={`inline-block px-3 py-1 rounded font-medium mt-1 ${
                      selectedRecord.passed
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                    }`}>
                      {selectedRecord.passed ? 'Passed' : 'Failed'}
                    </span>
                  </div>
                </div>

                {selectedRecord.findings && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">Findings</label>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedRecord.findings}</p>
                  </div>
                )}

                {selectedRecord.issues_found && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">Issues Found</label>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedRecord.issues_found}</p>
                  </div>
                )}

                {selectedRecord.follow_up_required && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-3">
                    <p className="text-yellow-800 dark:text-yellow-300 text-sm">⚠️ Follow-up required for this audit</p>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                <button
                  onClick={() => {
                    setShowDetailModal(false)
                    setSelectedRecord(null)
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleDeleteRecord(selectedRecord.id)
                    setShowDetailModal(false)
                    setSelectedRecord(null)
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                >
                  Delete Record
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
